// In-app PostHog (build file B10, section 3c). Reuses the ONE PostHog client
// the marketing site owns (client/src/lib/marketing/analytics.ts) — never
// calls posthog.init here.
//   - /app paths are opted into capture AND session replay (Travis, Oct 4:
//     record every app session). Replay privacy — which pages are readable
//     and which are fully masked — lives in client/src/lib/replay-privacy.ts
//     (default-deny); typed inputs are always masked by the shared init.
//   - identify(String(accountId)) after signup/login, so the anonymous
//     marketing visitor merges into the account; reset() on logout.
//   - The account id is the billing owner: a teammate identifies as their
//     owner's account. Admin impersonation never identifies.
// Never pass member data, message bodies, door codes or financial details.

import { getPosthog, registerPosthogPaths } from "@/lib/marketing/analytics";

export const APP_CAPTURE_PATH = (pathname: string): boolean => pathname.startsWith("/app");
export const APP_RECORD_PATH = (pathname: string): boolean => pathname.startsWith("/app");

let registered = false;
let identifiedAs: string | null = null;

/** Opt the app's own paths into capture/replay (once per page load). */
export function registerAppAnalyticsPaths(): void {
  if (registered) return;
  registered = true;
  registerPosthogPaths({ capture: APP_CAPTURE_PATH, record: APP_RECORD_PATH });
}

export interface AppMeForAnalytics {
  user?: { id: number } | null;
  activeMembership?: { ownerUserId: number } | null;
  impersonating?: boolean | null;
}

/** PostHog distinct_id for the signed-in session, or null when it must not identify. */
export function analyticsAccountId(me: AppMeForAnalytics | null | undefined): string | null {
  if (!me?.user || me.impersonating) return null;
  const accountId = me.activeMembership?.ownerUserId ?? me.user.id;
  return Number.isFinite(accountId) ? String(accountId) : null;
}

/** Identify the session as `accountId` (no-op without a PostHog key or when already identified). */
export async function identifyAppAccount(accountId: string): Promise<void> {
  registerAppAnalyticsPaths();
  if (identifiedAs === accountId) return;
  const posthog = await getPosthog();
  if (!posthog) return;
  posthog.identify(accountId);
  identifiedAs = accountId;
}

/** On logout: forget the identity so the next person on this browser starts anonymous. */
export async function resetAppAnalytics(): Promise<void> {
  identifiedAs = null;
  const posthog = await getPosthog();
  posthog?.reset();
}

/** Test hook. */
export function _resetAppAnalyticsForTests(): void {
  registered = false;
  identifiedAs = null;
}
